import {MutableRefObject, useCallback, useRef, useState} from 'react';
import {Employee, Workspace, WorkspacesApiService, useService} from '@innowise-group/core';
import {Elements, Node, OnLoadParams, XYPosition} from 'react-flow-renderer';
import {useTranslation} from 'react-i18next';
import {WorkspaceElementData} from '../../components/workspace-view';
import {PlanElementData} from '../../components/plan-element-view';
import {FloorsModalsFacadeService} from '../../services/floors-modals-facade';
import {FloorsEventEmitterService} from '../../services/floors-event-emitter';
import {DateRange} from '../../components/booking-workspace-mode-form';
import {
  updateWorkspaceData,
  OccupancyWorkspacePayload,
  toOccupancyWorkspacePayload,
  isOccupancyWorkspaceSave,
} from '../../utilities/update-workspace-data.utility';
import {updateWorkspaceMoveData, WorkspaceMovePayload} from '../../utilities/update-workspace-move-data.utility';
import {UpdatedWorkspaceDataResult} from '../../utilities/update-workspace-data.utility';
import {findCollidingWorkspace} from '../../utilities/check-workspaces-collision.utility';
import {checkWorkspaceRoomBounds} from '../../utilities/check-workspace-room-bounds.utility';

export interface WorkspaceMoveRevertSnapshot {
  previousCoordinates: Array<number[]>;
  previousPosition: XYPosition;
}

export interface PatchWorkspaceOptions {
  revertOnError?: boolean;
  previousCoordinates?: Array<number[]>;
  previousPosition?: XYPosition;
  alsoRefreshWorkspaceIds?: Array<string | number>;
}

export interface UseFlowElementsDragResult {
  dragOverHandler: (event: React.DragEvent<HTMLDivElement>) => void;
  dragHandler: (event: React.DragEvent<HTMLDivElement>) => void;
  nodeDragStartHandler: (event: React.MouseEvent<Element, MouseEvent>, node: Node<WorkspaceElementData>) => void;
  nodeDragStopHandler: (event: React.MouseEvent<Element, MouseEvent>, node: Node<WorkspaceElementData>) => void;
  loadHandler: (_reactFlowInstance: OnLoadParams) => void;
  reactFlowWrapper: MutableRefObject<HTMLDivElement>;
  hasInvalidWorkspaces: boolean;
  setValidation: () => void;
  resetValidation: () => void;
  getViewportCenter: () => XYPosition | null;
}

export const useFlowElementsDrag = (
  workspaceElements: Elements<WorkspaceElementData>,
  roomElements: Elements<PlanElementData>,
  isVirtual: boolean = false,
  bookWorkspaceDateRange: {range: DateRange},
  patchWorkspace: (
    workspaceData: UpdatedWorkspaceDataResult | WorkspaceMovePayload | OccupancyWorkspacePayload,
    options?: PatchWorkspaceOptions,
  ) => void,
  commitDraftWorkspace: (node: Node<WorkspaceElementData>) => void,
  workspacesApi: WorkspacesApiService,
  floorsEventEmitter: FloorsEventEmitterService,
  onError: (error: unknown) => void,
  revertWorkspacePosition?: (workspaceId: string | number, coordinates: Array<number[]>, position?: XYPosition) => void,
): UseFlowElementsDragResult => {
  const {t} = useTranslation();
  const floorsModalsFacade = useService(FloorsModalsFacadeService);
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const moveRevertSnapshotRef = useRef<WorkspaceMoveRevertSnapshot | null>(null);
  const [hasInvalidWorkspaces, setHasInvalidWorkspaces] = useState(false);
  const setValidation = useCallback(() => setHasInvalidWorkspaces(true), []);
  const resetValidation = useCallback(() => setHasInvalidWorkspaces(false), []);

  const onDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const getViewportCenter = useCallback((): XYPosition | null => {
    if (!reactFlowInstance || !reactFlowWrapper.current) {
      return null;
    }
    const bounds = reactFlowWrapper.current.getBoundingClientRect();
    return reactFlowInstance.project({
      x: bounds.width / 2,
      y: bounds.height / 2,
    });
  }, [reactFlowInstance]);

  const validateWorkspaceCollision = useCallback(
    (node: Node<WorkspaceElementData>) => {
      const {
        id: nodeId,
        position: {x, y},
        data: {width, height, rotateDegree},
      } = node;

      const collidedWorkspace = findCollidingWorkspace(
        {id: nodeId, x, y, width, height, rotateDegree},
        workspaceElements.map(({id, position, data}: Node<WorkspaceElementData>) => ({
          id,
          x: position.x,
          y: position.y,
          width: data.width,
          height: data.height,
          rotateDegree: data.rotateDegree,
          number: data.number,
        })),
      );

      if (collidedWorkspace) {
        setHasInvalidWorkspaces(true);
        floorsModalsFacade.openInfoModal(
          t('modals.workspacesIntersectionMessage', {number: collidedWorkspace.number}),
          t('modals.attention'),
          t('buttons.ok'),
        );
        return false;
      }

      return true;
    },
    [workspaceElements, floorsModalsFacade, t],
  );

  const validateWorkspaceRoomBounds = useCallback(
    (node: Node<WorkspaceElementData>) => {
      if (isVirtual) {
        return true;
      }

      const {
        position: {x, y},
        data: {width, height, rotateDegree},
      } = node;

      const roomBoundsResult = checkWorkspaceRoomBounds(
        {x, y, width, height, rotateDegree},
        roomElements.map(({data}) => ({id: data.id, polygon: data.polygon})),
      );

      if (roomBoundsResult !== 'ok') {
        setHasInvalidWorkspaces(true);
        const messageKey =
          roomBoundsResult === 'outsideRoom' ? 'modals.outsideRoomMessage' : 'modals.withinRoomMessage';
        floorsModalsFacade.openInfoModal(t(messageKey), t('modals.attention'), t('buttons.ok'));
        return false;
      }

      return true;
    },
    [floorsModalsFacade, isVirtual, roomElements, t],
  );

  const onDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const position = reactFlowInstance.project({
        x: Math.floor(event.clientX - reactFlowBounds.left),
        y: Math.floor(event.clientY - reactFlowBounds.top),
      });
      const workspace = workspaceElements.find(
        ({position: {x, y}, data: {width, height}}: Node<WorkspaceElementData>) => {
          const XPosition = x + width;
          const YPosition = y + height;
          return x <= position.x && position.x <= XPosition && y <= position.y && position.y <= YPosition;
        },
      );

      if (event.dataTransfer.getData('employee')) {
        const newEmployee: Employee = JSON.parse(event.dataTransfer.getData('employee'));

        if (workspace) {
          if (workspace.data?.isDraft) {
            return;
          }
          if (workspace.data?.employee?.fullName) {
            const currentName = newEmployee?.fullName;
            const previousName = workspace.data?.employee?.fullName;
            floorsModalsFacade
              .openConfirmationModal(
                t('modals.replaceEmployeeTitle'),
                t('modals.replaceEmployeeText', {previousName, currentName}),
                t('modals.replaceEmployeeConfirmationText'),
                t('buttons.cancel'),
              )
              .subscribe(() => {
                floorsEventEmitter.emit('editWorkspace', {
                  ...workspace.data,
                  employee: newEmployee,
                  status: 'Occupied',
                });
              });
          } else if (workspace?.data?.status === 'Booked') {
            floorsModalsFacade.openInfoModal(
              t('modals.hasWorkspaceBookedMessage'),
              t('modals.attention'),
              t('buttons.ok'),
            );
          } else if (isVirtual) {
            floorsEventEmitter.emit('editWorkspace', {
              ...workspace.data,
              employee: newEmployee,
              status: 'Remote',
            });
          } else if (bookWorkspaceDateRange) {
            floorsEventEmitter.emit('editWorkspace', {
              ...workspace.data,
              employee: newEmployee,
              status: 'Booked',
            });
          } else {
            floorsEventEmitter.emit('editWorkspace', {
              ...workspace.data,
              employee: newEmployee,
              status: 'Occupied',
            });
          }
        }
      } else {
        const {employee, status, comment, range}: Workspace = JSON.parse(event.dataTransfer.getData('workspace'));
        if (workspace && !workspace.data?.isDraft && workspace.data.status === 'Free') {
          const targetWorkspace = {...workspace.data, status, employee, comment, range};
          const payload = isOccupancyWorkspaceSave(status)
            ? toOccupancyWorkspacePayload(targetWorkspace)
            : updateWorkspaceData(targetWorkspace);
          patchWorkspace(payload);
        }
      }
    },
    [
      bookWorkspaceDateRange,
      floorsEventEmitter,
      floorsModalsFacade,
      isVirtual,
      patchWorkspace,
      reactFlowInstance,
      reactFlowWrapper,
      t,
      workspaceElements,
    ],
  );

  const onNodeDragStart = useCallback((_: React.MouseEvent<Element, MouseEvent>, node: Node<WorkspaceElementData>) => {
    const {position, data} = node;
    moveRevertSnapshotRef.current = {
      previousCoordinates: data.coordinates,
      previousPosition: {...position},
    };
  }, []);

  const onNodeDragStop = useCallback(
    (event: React.MouseEvent<Element, MouseEvent>, node: Node<WorkspaceElementData>) => {
      const revertSnapshot = moveRevertSnapshotRef.current;
      const isValidPlacement = validateWorkspaceCollision(node) && validateWorkspaceRoomBounds(node);

      if (!isValidPlacement) {
        if (!node.data.isDraft && revertSnapshot && revertWorkspacePosition) {
          revertWorkspacePosition(node.data.id, revertSnapshot.previousCoordinates, revertSnapshot.previousPosition);
        }
        moveRevertSnapshotRef.current = null;
        return;
      }

      resetValidation();

      const {
        position: {x, y},
        data,
      } = node;
      const nextCoordinates: Array<number[]> = [[x + 50, y + 25]];

      if (data.isDraft) {
        commitDraftWorkspace({
          ...node,
          position: {x, y},
          data: {
            ...data,
            coordinates: nextCoordinates,
          },
        });
        moveRevertSnapshotRef.current = null;
        return;
      }

      patchWorkspace(
        updateWorkspaceMoveData({
          id: data.id,
          coordinates: nextCoordinates,
          rotateDegree: data.rotateDegree,
          status: data.status,
        }),
        {
          revertOnError: true,
          previousCoordinates: revertSnapshot?.previousCoordinates ?? data.coordinates,
          previousPosition: revertSnapshot?.previousPosition,
        },
      );
      moveRevertSnapshotRef.current = null;
    },
    [
      validateWorkspaceCollision,
      validateWorkspaceRoomBounds,
      resetValidation,
      patchWorkspace,
      commitDraftWorkspace,
      revertWorkspacePosition,
    ],
  );

  const onLoad = useCallback((_reactFlowInstance: OnLoadParams) => {
    setReactFlowInstance(_reactFlowInstance);
    _reactFlowInstance.fitView();
  }, []);

  return {
    nodeDragStartHandler: onNodeDragStart,
    nodeDragStopHandler: onNodeDragStop,
    dragOverHandler: onDragOver,
    dragHandler: onDrop,
    loadHandler: onLoad,
    reactFlowWrapper,
    hasInvalidWorkspaces,
    setValidation,
    resetValidation,
    getViewportCenter,
  };
};
